import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search } from "lucide-react";

const fmt = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })} €`;
const fData = (s) => (s || "").split("-").reverse().join("/");

const Proposta = ({ p, sel, onSel }) => (
  <label className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer ${sel ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`linkfatt-prop-${p.id}`}>
    <input type="radio" className="mt-1" checked={sel} onChange={onSel} />
    <div className="min-w-0 flex-1 text-sm">
      <div className="flex justify-between gap-2"><b className="truncate">#{p.numero} · {fData(p.data)}</b><span className="tabular-nums">{fmt(p.imponibile)}</span></div>
      <div className="text-xs text-muted-foreground truncate">{p.controparte} · DDT già collegati {fmt(p.ddt_somma)}</div>
      {p.motivi.length > 0 && <div className="text-[11px] text-emerald-700">{p.motivi.join(" · ")}</div>}
    </div>
  </label>
);

export const CollegaDdtFatturaDialog = ({ did, onClose, onDone }) => {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = (query = "") => api.get(`/ddt/${did}/proposte-fatture`, { params: { q: query } })
    .then(({ data }) => { setD(data); setSel((s) => (s && data.proposte.some((p) => p.id === s) ? s : data.consigliata || "")); })
    .catch(() => toast.error("Impossibile caricare le fatture"));
  useEffect(() => {
    if (!did) return;
    setD(null); setSel(""); setQ("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [did]);

  const conferma = async () => {
    if (!sel) return toast.error("Scegli una fattura");
    setBusy(true);
    try {
      await api.post(`/fatture/${sel}/link-ddt`, { ddt_id: did });
      toast.success("DDT collegato alla fattura");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!did} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="linkfatt-dialog">
        <DialogHeader><DialogTitle>Collega DDT {d ? `n. ${d.ddt.numero || "s.n."}` : ""} a una fattura</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
          <div className="space-y-3 min-w-0">
            <div className="text-xs text-muted-foreground">{d.ddt.fornitore || "Fornitore non assegnato"} · {fData(d.ddt.data)} · imponibile {fmt(d.ddt.imponibile)}</div>
            {d.consigliata
              ? <div className="text-xs rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 p-2" data-testid="linkfatt-consigliata">Ho preselezionato la fattura più probabile: controlla e conferma.</div>
              : <div className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-800 p-2" data-testid="linkfatt-nessuna">Nessuna fattura sicura: scegli tra le proposte o cerca per numero o fornitore.</div>}
            <div className="flex gap-2">
              <Input placeholder="Cerca numero o fornitore" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)} data-testid="linkfatt-search" />
              <Button variant="outline" size="icon" onClick={() => load(q)} data-testid="linkfatt-search-btn"><Search className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2" data-testid="linkfatt-proposte">
              {d.proposte.map((p) => <Proposta key={p.id} p={p} sel={sel === p.id} onSel={() => setSel(p.id)} />)}
              {d.proposte.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">Nessuna fattura di acquisto trovata.</div>}
            </div>
            <Button className="w-full h-11" disabled={busy || !sel} onClick={conferma} data-testid="linkfatt-conferma">Collega alla fattura selezionata</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
